import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Typography, Button } from '@mui/material'

const columns = [
  { field: 'lineNumber', headerName: '#', width: 50, headerAlign: 'center', align: 'center' },
  { field: 'sku', headerName: 'NDC/Items #', flex: 0.7, minWidth: 130 },
  { field: 'orderOpenQty', headerName: 'Order Open Qty', width: 120, headerAlign: 'right', align: 'right' },
  { field: 'totalOpenQty', headerName: 'Total Open Qty', width: 120, headerAlign: 'right', align: 'right' },
  { field: 'activeStock', headerName: 'Active Stock', width: 100, headerAlign: 'right', align: 'right' },
  {
    field: 'reservedQty', headerName: 'Reserved Qty', width: 110, headerAlign: 'right', align: 'right',
    renderCell: (p) => <>{p.value || '-'}</>,
  },
  {
    field: 'committedQty', headerName: 'Committed', width: 100, headerAlign: 'right', align: 'right',
    renderCell: (p) => <>{p.value || 0}</>,
  },
  {
    field: 'ppsQty', headerName: 'On PPS', width: 90, headerAlign: 'right', align: 'right',
    renderCell: (p) => (
      <Typography sx={{
        fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem',
        color: p.value > 0 ? '#1a6b52' : '#b7b7b7',
        fontWeight: p.value > 0 ? 600 : 400,
      }}>
        {p.value || 0}
      </Typography>
    ),
  },
  {
    field: 'allocatedQty', headerName: 'Allocated Qty', width: 110, headerAlign: 'right', align: 'right',
    renderCell: (p) => <>{p.value || '-'}</>,
  },
  { field: 'availableToAllocate', headerName: 'Available To Allocate', width: 150, headerAlign: 'right', align: 'right' },
  {
    field: 'allocate', headerName: 'Allocate', width: 80, headerAlign: 'right', align: 'right',
    renderCell: (p) => (
      <Typography sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem', fontWeight: 600, color: '#262626' }}>
        {p.value}
      </Typography>
    ),
  },
]

function AllocationDetailsTableV2({ allocationLines, onAllocateAndClose, onAllocateAndNext, isSubmitting }) {
  // Filter to lines with open qty > 0
  const rows = (allocationLines || [])
    .filter((l) => l.orderOpenQty > 0)
    .map((l, idx) => ({
      id: l.lineNumber,
      lineNumber: idx + 1,
      sku: l.sku,
      orderOpenQty: l.orderOpenQty,
      totalOpenQty: l.totalOpenQty,
      activeStock: l.activeStock,
      reservedQty: l.reservedQty,
      committedQty: l.committedQty ?? 0,
      ppsQty: l.ppsQuantity ?? 0,
      allocatedQty: l.allocateQty,
      availableToAllocate: l.availableToAllocate,
      allocate: Math.min(l.orderOpenQty, l.availableToAllocate),
    }))

  if (rows.length === 0) return null

  return (
    <Box>
      <Typography sx={{ fontFamily: 'SanomatMedium', fontSize: '1.3rem', color: '#1a8a6a', mb: 1.5 }}>
        Allocation Details
      </Typography>
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #e7e7e7', borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[25]}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          disableColumnFilter
          disableRowSelectionOnClick
          autoHeight
          hideFooter={rows.length <= 25}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f8f8' },
            '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'AtlasGroteskRegular', fontSize: '0.78rem', fontWeight: 700, color: '#555' },
            '& .MuiDataGrid-cell': { fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem' },
          }}
        />
      </Box>

      {/* Bottom action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, mb: 2 }}>
        <Button
          variant='contained'
          onClick={onAllocateAndClose}
          disabled={isSubmitting}
          sx={{
            fontFamily: 'AtlasGroteskRegular', textTransform: 'uppercase', fontSize: '0.82rem',
            fontWeight: 700, letterSpacing: '0.05em',
            backgroundColor: '#1a6b52', color: '#fff', px: 4, py: 1.2,
            borderRadius: 6,
            '&:hover': { backgroundColor: '#145a4b' },
          }}
        >
          Allocate and Close
        </Button>
        <Button
          variant='outlined'
          onClick={onAllocateAndNext}
          disabled={isSubmitting}
          sx={{
            fontFamily: 'AtlasGroteskRegular', textTransform: 'uppercase', fontSize: '0.82rem',
            fontWeight: 700, letterSpacing: '0.05em',
            borderColor: '#1a6b52', color: '#1a6b52', px: 4, py: 1.2,
            borderRadius: 6,
            '&:hover': { borderColor: '#145a4b', color: '#145a4b', backgroundColor: '#f0faf5' },
          }}
        >
          Allocate and Next Order
        </Button>
      </Box>
    </Box>
  )
}

AllocationDetailsTableV2.propTypes = {
  allocationLines: PropTypes.array.isRequired,
  onAllocateAndClose: PropTypes.func.isRequired,
  onAllocateAndNext: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
}

export default AllocationDetailsTableV2
